'use client';

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Mail } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { PLATFORM_BRAND_TAGLINE, TenantBrand } from '@/components/brand/tenant-brand';
import { postAuthEventBeacon } from '@/lib/auth/event-beacon';
import {
  inviteSetPasswordPath,
  recoverySetPasswordPath,
} from '@/lib/auth/destinations';
import { createClient } from '@/lib/supabase/client';

type Mode = 'password' | 'magic-sent' | 'forgot-sent';

const GENERIC_FORGOT_MESSAGE =
  "If an account exists for that email, we've sent reset instructions.";

function implicitSetPasswordDestination(
  nextPath: string,
  type: 'invite' | 'recovery'
): string {
  if (nextPath.startsWith('/auth/set-password')) {
    return nextPath;
  }

  return type === 'recovery'
    ? recoverySetPasswordPath(nextPath)
    : inviteSetPasswordPath(nextPath);
}

// Magic-link guidance stays uniform for every address (#2537), including
// accounts that have not been activated.
function ActivationGuidance() {
  return (
    <div className="space-y-3 border-t border-border pt-5 text-sm leading-relaxed text-muted-foreground">
      <p>No email yet? Allow a few minutes and check your spam folder.</p>
      <p>
        Haven&apos;t set up your account yet?{' '}
        <a
          href="/start"
          className="font-medium text-accent-strong underline underline-offset-4 hover:text-foreground"
        >
          Set up your account
        </a>
        {' '}to receive sign-in and password reset emails.
      </p>
    </div>
  );
}

function GoogleLogo() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      width="18"
      height="18"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill="#4285F4"
        d="M21.35 11.1h-9.17v2.98h5.27c-.23 1.48-1.67 4.33-5.27 4.33-3.17 0-5.76-2.62-5.76-5.86 0-3.24 2.59-5.86 5.76-5.86 1.81 0 3.02.77 3.72 1.43l2.54-2.45C16.9 4.19 14.77 3.3 12.18 3.3 7.22 3.3 3.2 7.32 3.2 12.28s4.02 8.98 8.98 8.98c5.18 0 8.62-3.64 8.62-8.77 0-.59-.06-1.04-.15-1.49z"
      />
    </svg>
  );
}

export function LoginForm({
  initialError,
  nextPath,
  showGoogle,
  brandName = null,
  tagline = null,
  logoLightUrl = null,
  logoDarkUrl = null,
  appliedTheme = 'dark',
}: {
  initialError: string | null;
  nextPath: string;
  showGoogle: boolean;
  // Pre-auth brand (BRAND-4, #1765): resolved server-side from a /t/[slug] path
  // or the redemption-code cookie and threaded down so the wordmark and tagline
  // render the tenant brand. All null (no branded signal) falls back to the
  // platform default, so the plain /login is unchanged.
  brandName?: string | null;
  tagline?: string | null;
  logoLightUrl?: string | null;
  logoDarkUrl?: string | null;
  // The APPLIED root theme (cookie override + tenant lock/default), the same
  // value the root layout paints with, so the logo picks the variant that
  // matches the background the card actually renders on.
  appliedTheme?: 'light' | 'dark';
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mode, setMode] = useState<Mode>('password');
  const [showMagicLink, setShowMagicLink] = useState(false);
  const [loading, setLoading] = useState<null | 'password' | 'google' | 'magic' | 'forgot'>(null);
  const [error, setError] = useState<string | null>(initialError);

  const emailId = useId();
  const passwordId = useId();
  const magicEmailId = useId();
  const magicDisclosureId = useId();

  // #480: when a user clicks "Forgot password?" without typing an email, we
  // focus this input instead of silently no-op'ing. The pre-fix behavior
  // (disable the button at 50% opacity) read as clickable-but-weak because
  // users reaching for recovery typically haven't touched the email field.
  const emailInputRef = useRef<HTMLInputElement>(null);

  const supabase = useMemo(() => createClient(), []);

  useEffect(() => {
    let cancelled = false;
    const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const accessToken = fragment.get('access_token');
    const refreshToken = fragment.get('refresh_token');
    const type = fragment.get('type');

    // Supabase's default invite/recovery templates complete with an implicit
    // flow fragment (`#access_token=...`). If those links hit a server route
    // like /auth/callback first, the fragment is invisible to the server and
    // survives onto /login after the bounce. Recover the session client-side
    // so the user still lands on the set-password flow instead of a dead end.
    if (!accessToken || !refreshToken || (type !== 'invite' && type !== 'recovery')) {
      return () => {
        cancelled = true;
      };
    }

    const recoverImplicitSession = async () => {
      setError(null);
      const { error: sessionError } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      });

      if (cancelled) return;

      if (sessionError) {
        setError('Something went wrong with sign-in. Try again.');
        return;
      }

      const destination = implicitSetPasswordDestination(nextPath, type);
      window.location.replace(destination);
    };

    void recoverImplicitSession();

    return () => {
      cancelled = true;
    };
  }, [nextPath, supabase]);

  const handlePasswordSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setLoading('password');

    const { error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (signInError) {
      // Beacon BEFORE setError so the event still posts even if React batches
      // a re-render that suspends. Email is hashed server-side (#184); we do
      // not log plaintext credentials.
      postAuthEventBeacon('login.password.failure', {
        email,
        reason: signInError.message ?? 'unknown',
      });
      setError('Incorrect email or password');
      setLoading(null);
      return;
    }

    postAuthEventBeacon('login.password.success', { email });
    window.location.assign(nextPath);
  };

  const handleGoogleClick = async () => {
    setError(null);
    setLoading('google');
    const emailRedirectTo = new URL('/auth/callback', window.location.origin);
    emailRedirectTo.searchParams.set('next', nextPath);
    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: emailRedirectTo.toString(),
        queryParams: {
          prompt: 'select_account',
        },
      },
    });

    if (oauthError) {
      setError("Couldn't connect to Google. Try again or use another method.");
      setLoading(null);
    }
  };

  const handleMagicLinkSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setLoading('magic');

    const emailRedirectTo = new URL('/auth/confirm', window.location.origin);
    emailRedirectTo.searchParams.set('flow', 'magic');
    emailRedirectTo.searchParams.set('next', nextPath);
    const { error: otpError } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: emailRedirectTo.toString(),
        shouldCreateUser: false,
      },
    });

    // The UI deliberately ignores the provider result. Unknown emails are
    // rejected because shouldCreateUser is false, but must receive the same
    // locked sent state as existing accounts or this becomes an enumeration
    // oracle. Telemetry does preserve the distinction: login.magic.sent means
    // Auth accepted the handoff, while login.magic.success only fires when an
    // existing user completes /auth/confirm.
    if (!otpError) {
      postAuthEventBeacon('login.magic.sent', { email });
    }
    setMode('magic-sent');
    setLoading(null);
  };

  const handleForgotClick = async (event: React.MouseEvent) => {
    event.preventDefault();

    // #480: empty-email guard. trim() catches whitespace-only input — the
    // same guard Supabase would apply, surfaced here with a focus-moving
    // hint so the user immediately sees what to do next. No beacon fires;
    // this is a UX guard, not a recovery request.
    if (email.trim().length === 0) {
      setError('Enter your email to receive reset instructions.');
      emailInputRef.current?.focus();
      return;
    }

    setError(null);
    setLoading('forgot');

    // Route recovery links through /auth/confirm so the server can finalize
    // either the preferred token-hash flow or the legacy code fallback before
    // the user lands on /auth/set-password.
    const redirectTo = new URL('/auth/confirm', window.location.origin);
    redirectTo.searchParams.set('next', recoverySetPasswordPath(nextPath));
    await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectTo.toString(),
    });

    // Always show the same generic message (enumeration-safe). Ignore errors.
    setMode('forgot-sent');
    setLoading(null);
  };

  return (
    <main id="main" className="flex min-h-screen flex-col items-center justify-center px-4 py-10">
      <Card className="w-full max-w-md gap-6 p-6 shadow-lg sm:p-8">
        <div className="space-y-2 text-center">
          <h1>
            <TenantBrand
              size="lg"
              className="justify-center"
              brandName={brandName}
              logoLightUrl={logoLightUrl}
              logoDarkUrl={logoDarkUrl}
              theme={appliedTheme}
            />
          </h1>
          {mode === 'password' && (
            <p className="text-sm text-muted-foreground">
              {tagline ?? PLATFORM_BRAND_TAGLINE}
            </p>
          )}
        </div>

        {error && (
          <p
            role="alert"
            className="rounded-md border border-[color-mix(in_srgb,var(--danger)_40%,transparent)] bg-[var(--danger-bg)] px-3 py-2 text-sm text-[var(--danger)]"
          >
            {error}
          </p>
        )}

        {mode === 'magic-sent' && (
          <div className="rounded-lg border border-border bg-muted/40 p-4 text-center space-y-2">
            <p className="text-sm font-medium">Check your email</p>
            <p className="text-sm text-muted-foreground">
              We sent a magic link to <span className="font-medium text-foreground">{email}</span>
            </p>
            <ActivationGuidance />
          </div>
        )}

        {mode === 'forgot-sent' && (
          <div className="space-y-6">
            <div role="status" className="space-y-4 text-center">
              <div className="mx-auto flex size-12 items-center justify-center rounded-xl border border-brand/25 bg-brand/10 text-accent-strong">
                <Mail className="size-6" strokeWidth={1.5} aria-hidden="true" />
              </div>
              <div className="space-y-2">
                <h2 className="font-heading text-2xl font-semibold tracking-tight">Check your email</h2>
                <p className="text-sm leading-relaxed text-muted-foreground">{GENERIC_FORGOT_MESSAGE}</p>
              </div>
            </div>
            <p className="border-t border-border pt-5 text-sm leading-relaxed text-muted-foreground">
              No email yet? Allow a few minutes and check your spam folder.
            </p>
            <a
              href={`/login?next=${encodeURIComponent(nextPath)}`}
              className="flex min-h-11 items-center justify-center gap-2 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <ArrowLeft className="size-4" aria-hidden="true" />
              Back to sign in
            </a>
          </div>
        )}

        {mode === 'password' && (
          <>
            {/* #2537: first-time learners arriving at the sign-in front door
                have no credentials yet; route them into the onboarding funnel
                before they burn attempts on a password they never set. */}
            <p className="rounded-lg border border-brand/30 bg-brand/10 px-3 py-2 text-center text-sm">
              First time here?{' '}
              <a
                href="/start"
                className="font-semibold text-accent-strong underline-offset-2 hover:underline"
              >
                Start here
              </a>
            </p>

            <form onSubmit={handlePasswordSubmit} className="space-y-4" aria-busy={loading === 'password'}>
              <div className="space-y-2">
                <label htmlFor={emailId} className="text-sm font-medium">
                  Email
                </label>
                <Input
                  id={emailId}
                  ref={emailInputRef}
                  size="xl"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-baseline justify-between">
                  <label htmlFor={passwordId} className="text-sm font-medium">
                    Password
                  </label>
                  <button
                    type="button"
                    onClick={handleForgotClick}
                    disabled={loading !== null}
                    className="text-xs text-accent-strong underline-offset-2 hover:underline disabled:opacity-50"
                  >
                    {loading === 'forgot' ? 'Sending…' : 'Forgot password?'}
                  </button>
                </div>
                <Input
                  id={passwordId}
                  size="xl"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
              </div>

              <Button type="submit" size="xl" className="w-full" disabled={loading !== null}>
                {loading === 'password' ? 'Signing in…' : 'Sign in'}
              </Button>
            </form>

            {showGoogle && (
              <div className="flex items-center gap-2" aria-hidden="true">
                <div className="h-px flex-1 bg-border" />
                <span className="text-xs uppercase tracking-wider text-muted-foreground">or</span>
                <div className="h-px flex-1 bg-border" />
              </div>
            )}

            {showGoogle && (
              <Button
                type="button"
                variant="outline"
                size="xl"
                className="flex w-full items-center gap-2"
                onClick={handleGoogleClick}
                disabled={loading !== null}
              >
                <GoogleLogo />
                {loading === 'google' ? 'Connecting…' : 'Continue with Google'}
              </Button>
            )}

            <div className="text-center">
              <button
                type="button"
                aria-expanded={showMagicLink}
                aria-controls={magicDisclosureId}
                onClick={() => setShowMagicLink((current) => !current)}
                className="text-sm text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
              >
                {showMagicLink ? 'Hide magic link option' : 'Or send a magic link instead'}
              </button>
            </div>

            {showMagicLink && (
              <form
                id={magicDisclosureId}
                onSubmit={handleMagicLinkSubmit}
                className="space-y-3"
                aria-busy={loading === 'magic'}
              >
                <label htmlFor={magicEmailId} className="text-sm font-medium">
                  Email for magic link
                </label>
                <Input
                  id={magicEmailId}
                  size="xl"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
                <Button
                  type="submit"
                  variant="outline"
                  size="xl"
                  className="w-full"
                  disabled={loading !== null}
                >
                  {loading === 'magic' ? 'Sending link…' : 'Send magic link'}
                </Button>
              </form>
            )}
          </>
        )}
      </Card>
    </main>
  );
}
